// src/pages/AddAIPrompts.js
import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, X, Save, BookOpen, PenTool, Briefcase, Zap, Users, TrendingUp, MessageCircle } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Link } from 'react-router-dom'; // Assuming you'll use React Router
import { db } from '../firebase/config';
import Header from '../Components/Header';
import toast from 'react-hot-toast';

// --- Icons Mapping ---
const categoryIcons = {
  "Writing": PenTool,
  "CEO": Briefcase,
  "Automation": Zap,
  "HR": Users,
  "Sales": TrendingUp,
  "Marketing": MessageCircle,
  "All": BookOpen // Default icon for 'All' category
};

const AddAIPrompts = ({setIsSidebarOpen,isSidebarOpen}) => {
  const [prompts, setPrompts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = ["All", "Writing", "CEO", "Automation", "HR", "Sales", "Marketing"];

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Writing',
    tags: '', 
  });

  // Fetch prompts from Firestore on component mount
  useEffect(() => {
    const fetchPrompts = async () => {
      setLoading(true);
      setError(null);
      try {
        const querySnapshot = await getDocs(collection(db, "aiprompts"));
        const fetchedPrompts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPrompts(fetchedPrompts);
      } catch (err) {
        console.error("Error fetching prompts: ", err);
        setError("Failed to load prompts for administration. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPrompts();
  }, []); // Empty dependency array means this runs once on mount

  const filteredPrompts = prompts.filter(prompt => {
    const matchesSearch = prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          prompt.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (prompt.tags && Array.isArray(prompt.tags) && prompt.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    const matchesCategory = selectedCategory === 'All' || prompt.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Handle form submission (add/edit)
  const handleSubmit = async () => {
    if (!formData.title || !formData.description ) {
      toast.error('Please fill in all required fields: Title, Description, and Prompt.');
      return;
    }

    const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    const promptData = {
      ...formData,
      tags: tagsArray
    };
    const promptCollectionRef = collection(db, 'aiprompts');

    try {
      if (editingPrompt) {
        // Update existing prompt
        const promptDocRef = doc(db, 'aiprompts', editingPrompt.id);
        await updateDoc(promptDocRef, promptData);
        setPrompts(prompts.map(p =>
          p.id === editingPrompt.id
            ? { ...promptData, id: editingPrompt.id }
            : p
        ));
        toast.success('Prompt updated successfully!');
      } else {
        // Add new prompt
        const docRef = await addDoc(promptCollectionRef, promptData);
        setPrompts([...prompts, { ...promptData, id: docRef.id }]);
        toast.success('Prompt added successfully!');
      }
      resetForm();
    } catch (err) {
      console.error("Error saving prompt: ", err);
      toast.error("Failed to save prompt. Please try again.");
    }
  };

  // Reset form state and close modal
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'Writing',
      tags: '',
    });
    setShowModal(false);
    setEditingPrompt(null);
  };

  // Populate form for editing
  const handleEdit = (prompt) => {
    setFormData({
      ...prompt,
      tags: prompt.tags ? prompt.tags.join(', ') : '' // Convert tags array to string for input
    });
    setEditingPrompt(prompt);
    setShowModal(true);
  };

  // Delete prompt
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this prompt?')) {
      try {
        const promptDocRef = doc(db, 'aiprompts', id);
        await deleteDoc(promptDocRef);
        setPrompts(prompts.filter(p => p.id !== id));
        toast.success('Prompt deleted successfully!');
      } catch (err) {
        console.error("Error deleting prompt: ", err);
        toast.error("Failed to delete prompt. Please try again.");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a1344' }}>
        <p className="text-white text-lg">Loading prompts for admin panel...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a1344' }}>
        <p className="text-red-400 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
         <div className="flex flex-col">
        <Header
          PageName={"Mes Prompts"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>
      <div className="bg-bgColor p-6 rounded-lg border border-gray-600 h-[90dvh]">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Mes Prompts - Admin</h1>
            <p className="text-blue-200">Manage your AI prompt library</p>
          </div>
          <div className="flex gap-4">
           
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Prompt
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categories.map(cat => (
              <option key={cat} value={cat} className="bg-gray-800">{cat}</option>
            ))}
          </select>
        </div>

        {/* Prompts Table */}
        <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-white font-semibold">Title</th>
                  <th className="text-left p-4 text-white font-semibold">Category</th>
                  <th className="text-left p-4 text-white font-semibold">Tags</th>
                  <th className="text-left p-4 text-white font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrompts.map(prompt => (
                  <tr key={prompt.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4">
                      <div>
                        <h3 className="text-white text-start font-medium">{prompt.title}</h3>
                        <p className="max-w-[700px] text-gray-300 text-start text-sm mt-1 truncate">{prompt.description}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-blue-600/20 text-blue-200 rounded-full text-xs">
                        {prompt.category}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {prompt.tags && Array.isArray(prompt.tags) && prompt.tags.map(tag => (
                          <span key={tag} className="px-2 py-1 bg-gray-600/20 text-gray-300 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(prompt)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-600/20 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(prompt.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-600/20 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredPrompts.length === 0 && (
            <div className="text-center py-12 text-gray-400">No prompts found.</div>
          )}
        </div>
      </div>

      {/* Modal for Add/Edit Prompt */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[#19223c] backdrop-blur-lg rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingPrompt ? 'Edit Prompt' : 'Add New Prompt'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-start text-white font-medium mb-2">Title <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter prompt title"
                />
              </div>

              <div>
                <label className="block text-start text-white font-medium mb-2">Description <span className="text-red-400">*</span></label>
                <textarea
                  type="text"
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of the prompt"
                />
              </div>

              <div>
                <label className="block text-start text-white font-medium mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categories.filter(cat => cat !== 'All').map(cat => (
                    <option key={cat} value={cat} className="bg-gray-800">{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-start text-white font-medium mb-2">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="SEO, Content, Marketing"
                />
              </div>


              <div className="flex gap-4 pt-4">
                <button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {editingPrompt ? 'Update' : 'Save'} Prompt
                </button>
                <button
                  onClick={resetForm}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddAIPrompts;